import { useState } from 'react';
import { Box } from 'lucide-react';
import { apiService, PackBlockBox, PackBlockDTO } from '@/services/api.service';
import { ConfigCatalog } from '@/components/config/ConfigCatalog';
import { BedrockModelViewer } from '@/components/config/BedrockModelViewer';
import { BlockEditor } from '@/components/config/BlockEditor';
import { BoxEditor } from '@/components/config/BoxEditor';
import styles from '@/components/config/PackDetail.module.scss';

export function BlocksPage() {
  return (
    <ConfigCatalog<PackBlockDTO>
      title="Blocks"
      icon={Box}
      kind="block"
      load={async (g) => (await apiService.getPackBlocks(g)).data?.blocks ?? []}
      keyOf={(b) => b.identifier}
      identifierOf={(b) => b.identifier}
      subtitleOf={(b) => b.pack}
      thumbnailOf={(b) => b.thumbnail}
      matches={(b, q) =>
        b.identifier.toLowerCase().includes(q) ||
        b.pack.toLowerCase().includes(q) ||
        (b.geometry || '').toLowerCase().includes(q)
      }
      statusOf={(b) => (
        <span className={`${styles.chip} ${b.geometry ? styles.chipPrimary : ''}`}>
          {b.geometry ? '3D' : b.sourceType === 'resource_only' ? 'RP' : 'BP'}
        </span>
      )}
      sourceOf={(b) => (b.sourceType === 'block' ? b.source : null)}
      renderDetail={(b, g, reload, group, visible) => <BlockDetail block={b} groupId={g} reload={reload} readonlyResource={!!group?.resourceReadonly} visible={visible !== false} />}
    />
  );
}

function BlockDetail({ block, groupId, reload, readonlyResource, visible }: { block: PackBlockDTO; groupId: string; reload: () => void; readonlyResource: boolean; visible: boolean }) {
  const primary = block.thumbnail || Object.values(block.textures || {})[0] || null;
  const [collision, setCollision] = useState<PackBlockBox>(block.collision || { mode: 'full', origin: null, size: null });
  const [selection, setSelection] = useState<PackBlockBox>(block.selection || { mode: 'full', origin: null, size: null });
  const [preview, setPreview] = useState<{
    textureUrl: string | null;
    texturePath: string | null;
    geometryText: string | null;
    geometryFile: string | null;
    geometryId: string;
  }>({ textureUrl: null, texturePath: null, geometryText: null, geometryFile: null, geometryId: '' });
  const hasTexOverride = !!(preview.textureUrl || preview.texturePath);
  return (
    <div>
      <div className={styles.head}>
        <div>
          <div className={styles.name}>{block.identifier}</div>
          <div className={styles.pack}>{block.pack}</div>
        </div>
        <div className={styles.chips}>
          {block.geometry && <span className={`${styles.chip} ${styles.chipPrimary}`}>Géométrie</span>}
          {block.renderMethod && <span className={styles.chip}>{block.renderMethod}</span>}
        </div>
      </div>

      {visible && (
        <div className={styles.viewerWrap}>
          <BedrockModelViewer
            groupId={groupId}
            geometryFile={preview.geometryText ? null : preview.geometryFile ?? block.geometryFile}
            geometryId={preview.geometryId || block.geometry}
            texturePath={preview.textureUrl ? null : preview.texturePath ?? primary}
            faceTextures={hasTexOverride ? undefined : block.textures}
            renderMethod={block.renderMethod}
            overrideTextureUrl={preview.textureUrl}
            overrideGeometryText={preview.geometryText}
            showGround
            collisionBox={collision}
            selectionBox={selection}
          />
          <div className={styles.boxOverlay}>
            <BoxEditor label="Box de collision" color="#ff5555" box={collision} onChange={setCollision} />
            <BoxEditor label="Hitbox (sélection)" color="#38bdf8" box={selection} onChange={setSelection} />
          </div>
        </div>
      )}

      <BlockEditor block={block} groupId={groupId} onSaved={reload} collision={collision} selection={selection} readonlyResource={readonlyResource} onPreviewOverride={setPreview} />
    </div>
  );
}
